/**
 * The color math the palette generator needs, and nothing more: hex in, OKLCH
 * to think in, hex out, plus WCAG contrast to judge the result.
 *
 * OKLCH because its lightness is perceptual — stepping L by a fixed amount
 * produces the same visible step whatever the hue — which is what makes a
 * "surface ladder" (background → card → muted → border) transferable from one
 * brand color to another. The conversions are the CSS Color 4 reference
 * matrices; `color.test.ts` checks them against the oklch values in
 * @loot-os/ui globals.css and the hex table in storefrontTokens.ts.
 *
 * Parsing is deliberately hex-only. Everything upstream (the dashboard's color
 * picker, the token defaults) is hex, and the one place a real parser would
 * matter — arbitrary CSS in a stored theme — is out of scope by design.
 */

/** sRGB channels as 0..1 floats. May be out of range when converted from OKLCH. */
export type Rgb = readonly [r: number, g: number, b: number];

/** Lightness 0..1, chroma ≥ 0, hue in degrees [0, 360). */
export interface Oklch {
  L: number;
  C: number;
  h: number;
}

const HEX = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;

/** `#rgb` or `#rrggbb` → channels, or null for anything else. */
export function parseHex(hex: string): Rgb | null {
  const digits = HEX.exec(hex.trim())?.[1];
  if (!digits) return null;
  const full = digits.length === 3 ? [...digits].map((d) => d + d).join("") : digits;
  return [
    parseInt(full.slice(0, 2), 16) / 255,
    parseInt(full.slice(2, 4), 16) / 255,
    parseInt(full.slice(4, 6), 16) / 255,
  ];
}

export function isHexColor(value: string): boolean {
  return HEX.test(value.trim());
}

/** Channels → lowercase `#rrggbb`. Out-of-range channels are clamped — see `clipToHex`. */
export function formatHex(rgb: Rgb): string {
  return (
    "#" +
    rgb
      .map((c) =>
        Math.round(Math.min(1, Math.max(0, c)) * 255)
          .toString(16)
          .padStart(2, "0"),
      )
      .join("")
  );
}

// ---- sRGB transfer ------------------------------------------------------------

function toLinear(c: number): number {
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

function fromLinear(c: number): number {
  return c <= 0.0031308 ? 12.92 * c : 1.055 * c ** (1 / 2.4) - 0.055;
}

// ---- OKLab (Björn Ottosson; matrices as published in CSS Color 4) ------------

type Mat3 = readonly [Rgb, Rgb, Rgb];

const LINEAR_TO_LMS: Mat3 = [
  [0.4122214708, 0.5363325363, 0.0514459929],
  [0.2119034982, 0.6806995451, 0.1073969566],
  [0.0883024619, 0.2817188376, 0.6299787005],
];
const LMS_TO_LAB: Mat3 = [
  [0.2104542553, 0.793617785, -0.0040720468],
  [1.9779984951, -2.428592205, 0.4505937099],
  [0.0259040371, 0.7827717662, -0.808675766],
];
const LAB_TO_LMS: Mat3 = [
  [1, 0.3963377774, 0.2158037573],
  [1, -0.1055613458, -0.0638541728],
  [1, -0.0894841775, -1.291485548],
];
const LMS_TO_LINEAR: Mat3 = [
  [4.0767416621, -3.3077115913, 0.2309699292],
  [-1.2684380046, 2.6097574011, -0.3413193965],
  [-0.0041960863, -0.7034186147, 1.707614701],
];

function mul(m: Mat3, v: Rgb): [number, number, number] {
  return [
    m[0][0] * v[0] + m[0][1] * v[1] + m[0][2] * v[2],
    m[1][0] * v[0] + m[1][1] * v[1] + m[1][2] * v[2],
    m[2][0] * v[0] + m[2][1] * v[1] + m[2][2] * v[2],
  ];
}

export function rgbToOklch(rgb: Rgb): Oklch {
  const linear: Rgb = [toLinear(rgb[0]), toLinear(rgb[1]), toLinear(rgb[2])];
  const lms = mul(LINEAR_TO_LMS, linear);
  const [L, a, b] = mul(LMS_TO_LAB, [Math.cbrt(lms[0]), Math.cbrt(lms[1]), Math.cbrt(lms[2])]);
  const C = Math.hypot(a, b);
  // Below this chroma the hue is numerical noise; pin it so greys compare equal.
  const h = C < 1e-4 ? 0 : normalizeHue((Math.atan2(b, a) * 180) / Math.PI);
  return { L, C, h };
}

/** OKLCH → sRGB channels, *not* clamped: a channel outside 0..1 means out of gamut. */
export function oklchToRgb({ L, C, h }: Oklch): Rgb {
  const rad = (h * Math.PI) / 180;
  const lmsRoot = mul(LAB_TO_LMS, [L, C * Math.cos(rad), C * Math.sin(rad)]);
  const linear = mul(LMS_TO_LINEAR, [lmsRoot[0] ** 3, lmsRoot[1] ** 3, lmsRoot[2] ** 3]);
  return [fromLinear(linear[0]), fromLinear(linear[1]), fromLinear(linear[2])];
}

export function hexToOklch(hex: string): Oklch | null {
  const rgb = parseHex(hex);
  return rgb ? rgbToOklch(rgb) : null;
}

export function normalizeHue(h: number): number {
  const wrapped = h % 360;
  return wrapped < 0 ? wrapped + 360 : wrapped;
}

// ---- gamut -------------------------------------------------------------------

// A hair outside 0..1 is rounding, not an out-of-gamut color — `#ffffff`
// round-trips to 1.0000000002 on some channels.
const GAMUT_EPSILON = 1e-4;

export function isInGamut(rgb: Rgb): boolean {
  return rgb.every((c) => c >= -GAMUT_EPSILON && c <= 1 + GAMUT_EPSILON);
}

/**
 * Hard channel clamp. This is what browsers (and the hand-maintained hex table)
 * do with an out-of-gamut oklch() value; it shifts hue and lightness, so the
 * generator never uses it for derived colors — see `toGamutHex`.
 */
export function clipToHex(rgb: Rgb): string {
  return formatHex(rgb);
}

/**
 * The nearest in-gamut color with the *same* lightness and hue, found by
 * reducing chroma. That is the CSS Color 4 gamut-mapping idea in its simplest
 * form: a saturated seed on a surface ladder still lands at the lightness the
 * ladder asked for, merely a little greyer, so contrast promises hold.
 */
export function toGamutRgb(color: Oklch): Rgb {
  const direct = oklchToRgb(color);
  if (isInGamut(direct)) return direct;
  // Chroma 0 is always representable (a grey at this L), so a bisection over
  // [0, C] converges on the gamut boundary.
  let lo = 0;
  let hi = color.C;
  let best = oklchToRgb({ ...color, C: 0 });
  for (let i = 0; i < 24; i++) {
    const mid = (lo + hi) / 2;
    const candidate = oklchToRgb({ ...color, C: mid });
    if (isInGamut(candidate)) {
      best = candidate;
      lo = mid;
    } else {
      hi = mid;
    }
  }
  return best;
}

export function toGamutHex(color: Oklch): string {
  return formatHex(toGamutRgb(color));
}

// ---- contrast (WCAG 2.x) -----------------------------------------------------

export function relativeLuminance(rgb: Rgb): number {
  return 0.2126 * toLinear(rgb[0]) + 0.7152 * toLinear(rgb[1]) + 0.0722 * toLinear(rgb[2]);
}

/** WCAG 2 contrast ratio, 1..21. Order doesn't matter. */
export function contrastRatio(a: Rgb, b: Rgb): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05);
}

/** `contrastRatio` for two hex strings; NaN when either fails to parse. */
export function hexContrast(a: string, b: string): number {
  const ra = parseHex(a);
  const rb = parseHex(b);
  return ra && rb ? contrastRatio(ra, rb) : NaN;
}
